"use client";

import { c as _c } from "react/compiler-runtime";
import React, { type ReactNode } from "react";
import { Provider as JotaiProvider } from "jotai";
import { config, dappmetadata, wagmiAdapter } from "@/config/index";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
export { LiquidityPositionsProvider, useLiquidityPositionsContext, type DexLoadingState, type LiquidityPositionsState } from './LiquidityPositionsContext';
import { type Config, type State, WagmiProvider } from "wagmi";
import { hashFn } from "@wagmi/core/query";
import { createAppKit } from "@reown/appkit/react";
import { chainDefaults } from "@/constants/chain-specific/chains";
import { shibarium, dogechain, base, mainnet } from "@reown/appkit/networks";
import { createIDBPersister } from "@/lib/persistor/idb";

// Setup queryClient with optimized defaults for NFT data
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryKeyHashFn: hashFn,
      structuralSharing: false,
      // Enhanced cache settings for NFT data
      staleTime: 1000 * 60 * 5,
      // 5 minutes
      gcTime: 1000 * 60 * 30,
      // 30 minutes (renamed from cacheTime in v5)
      retry: (failureCount, error: any) => {
        // Don't retry on metadata errors (404s, etc.)
        if (error?.name === 'MetadataError') return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      refetchOnReconnect: true
    },
    mutations: {
      retry: 1
    }
  }
});

// Create IndexedDB persister for cache persistence
const persister = createIDBPersister('chewy-nft-cache-v1');
export const projectId = process.env.NEXT_PUBLIC_PROJECT_ID;
if (!projectId) throw new Error("Project ID is not defined");
declare module "wagmi" {
  interface Register {
    config: typeof config;
  }
}

// Initialize AppKit singleton - must be called before any useAppKit hooks
// AppKit is designed to work on both client and server
let appkitModal: any;
try {
  appkitModal = createAppKit({
    adapters: [wagmiAdapter],
    networks: [shibarium, dogechain, base, mainnet],
    defaultNetwork: shibarium,
    metadata: dappmetadata,
    enableWalletConnect: true,
    themeMode: "dark",
    projectId,
    themeVariables: {
      "--w3m-accent": "#fc790e",
      "--w3m-color-mix": "#2a3344",
      "--w3m-color-mix-strength": 40,
      "--w3m-border-radius-master": "2px"
    },
    features: {
      analytics: true,
      swaps: false,
      onramp: false
    },
    chainImages: chainDefaults.chainImages,
    debug: process.env.NODE_ENV === 'development'
  });
} catch (error) {
  console.error('❌ Failed to initialize AppKit:', error);
  appkitModal = null;
}
export { appkitModal };
export default function Web3Provider(t0) {
  const $ = _c(7);
  const {
    children,
    initialState
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      persister
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ReactQueryDevtools initialIsOpen={false} />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <PersistQueryClientProvider client={queryClient} persistOptions={t1}>{children}{t2}</PersistQueryClientProvider>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== initialState || $[5] !== t3) {
    t4 = <WagmiProvider config={config as Config} initialState={initialState}>{t3}</WagmiProvider>;
    $[4] = initialState;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}